import { useAuthenticator } from '@aws-amplify/ui-react';
import { FC, ReactNode, useCallback } from 'react';
import { Breadcrumbs, BreadcrumbsContextProvider } from '@/breadcrumbs';
import { Header } from '@/components/header';
import { Sidebar } from '@/components/sidebar';
import { strings } from '@/constants/strings';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useNavItems } from '@/hooks/use-nav-items';
import { useSidebarConfig } from '@/hooks/use-sidebar-config';
import { useUser } from '@/hooks/use-user';
import { useUserOrganisation } from '@/hooks/use-user-organisation';
import { useUserRole } from '@/hooks/use-user-role';

export type AuthenticatedLayoutProps = { children: ReactNode };

export const AuthenticatedLayout: FC<AuthenticatedLayoutProps> = ({
  children,
}) => {
  const { signOut } = useAuthenticator();
  const { user } = useUser();
  const { organisation } = useUserOrganisation();
  const { isPlatformAdmin } = useUserRole();
  const navItems = useNavItems();
  const { nested } = useSidebarConfig();

  // Update document title based on current route
  useDocumentTitle();

  const handleSignOutClick = useCallback(() => {
    // delay call sign out since there's a bug where it doesn't trigger
    // after having submitted a form
    setTimeout(() => {
      signOut();
    }, 1);
  }, [signOut]);

  return (
    <div className="flex h-full flex-row">
      <Sidebar
        darkVariant={isPlatformAdmin}
        navItems={navItems}
        nestedConfig={nested}
        subtitle={
          isPlatformAdmin ? strings.sidebar.subtitle : organisation?.name
        }
        title={strings.sidebar.title}
      />
      <div className="flex grow flex-col">
        <Header onSignOutClick={handleSignOutClick} user={user} />
        <main className="h-full overflow-y-auto p-10 pt-0">
          <BreadcrumbsContextProvider>
            <Breadcrumbs />
            {children}
          </BreadcrumbsContextProvider>
        </main>
      </div>
    </div>
  );
};
